import {
    StyleSheet,
    TouchableOpacity,
    Text,
    View,
    useWindowDimensions
} from "react-native";
import { useState, useEffect } from 'react';
import { Ionicons } from '@expo/vector-icons';
import moment from 'moment';
import { getStorage, setStorage } from '../storage';

// ─── 전역 다크모드 (상단 토글 하나로 전 화면 실시간 전환) ───
// 모듈 레벨 값 + 구독. 화면은 useDarkMode()로 {dark, setDark(boolean), toggle} 사용.
// setDark는 boolean 전역 setter라 기존 화면의 setDark(next) 호출과 호환됨.
let _dark = false;
let _darkLoaded = false;
const _darkSubs = new Set<(v: boolean) => void>();
const _emitDark = () => { _darkSubs.forEach((fn) => fn(_dark)); };
export const setGlobalDark = (v: boolean) => {
    _dark = !!v;
    try { setStorage('darkMode', _dark); } catch {}
    _emitDark();
};
export const toggleGlobalDark = () => setGlobalDark(!_dark);
export function useDarkMode() {
    const [dark, setLocal] = useState(_dark);
    useEffect(() => {
        _darkSubs.add(setLocal);
        if (!_darkLoaded) {
            _darkLoaded = true;
            getStorage('darkMode').then((v: any) => {
                if (v !== null && v !== undefined) { _dark = !!v; _emitDark(); }
            });
        } else {
            setLocal(_dark);
        }
        return () => { _darkSubs.delete(setLocal); };
    }, []);
    return { dark, setDark: setGlobalDark, toggle: toggleGlobalDark };
}

// ─── 반응형 스케일 (비율 + 상하한 clamp) — 전 화면 공통 ───
// 화면 폭에 비례해 글자/여백/요소 크기를 조정. BASE_W에서 1.0배, [MIN,MAX]로 clamp.
// 사용법: const { s } = useScale(); ...  fontSize: s(16),  그리고 StyleSheet는 makeStyles(s) 팩토리로.
// ─── 자세(체위) 기록 ───────────────────────────────────────────────
// 기저귀 기록과는 **별개**의 기록이다(Posture.tsx / POSTURE_RECORD).
// 값은 센서 판정(gwserver/gwcalcV4.js 의 POSTURE_REF)과 **같은 코드**를 쓴다.
// 그래야 기록지의 자세와 SENSOR_HIS.POSTURE 를 그대로 대조할 수 있다.
// 한글은 화면 표시용 라벨일 뿐이니, 라벨만 바꾸고 코드는 건드리지 말 것.
export const POSTURE_OPTS: { v: string; label: string }[] = [
    { v: 'standup',  label: '일어섬' },
    { v: 'layright', label: '오른쪽' },
    { v: 'layleft',  label: '왼쪽' },
    { v: 'laydown',  label: '누움' },
    { v: 'flipover', label: '엎드림' },
];
export const POSTURE_LABEL: Record<string, string> =
    POSTURE_OPTS.reduce((m, o) => { m[o.v] = o.label; return m; }, {} as Record<string, string>);
// 저장된 코드를 한글로. 값이 없거나 모르는 코드면 코드 그대로(또는 '-') 보여준다.
export const postureText = (v: any): string => {
    if (v === null || v === undefined || String(v).trim() === '') return '-';
    return POSTURE_LABEL[String(v)] || String(v);
};

export const SCALE_BASE_W = 1440;
export const SCALE_MIN = 0.85;
export const SCALE_MAX = 1.25;
export function useScale() {
    const { width } = useWindowDimensions();
    const scale = Math.min(Math.max(width / SCALE_BASE_W, SCALE_MIN), SCALE_MAX);
    const s = (n: number) => Math.round(n * scale);
    return { scale, s };
}
export type ScaleFn = (n: number) => number;

// ─── 공통 페이지네이션 (기획서 slide26 목업: 총 N건 · Rows per page · 처음|이전|숫자|다음|마지막) ───
// 사용법: 리스트를 직접 slice 해서 DataTable에 넘기고, 표 아래에 <TablePagination .../> 배치.
export const TablePagination = ({
    rowCount, perPage, page, onChangePage, onChangePerPage,
    T, s, accent = '#38BDF8', options = [10, 15, 20, 25, 30],
}: {
    rowCount: number; perPage: number; page: number;
    onChangePage: (p: number) => void; onChangePerPage: (n: number) => void;
    T: any; s: ScaleFn; accent?: string; options?: number[];
}) => {
    const [open, setOpen] = useState(false);
    const totalPages = Math.max(1, Math.ceil(rowCount / perPage));
    const cur = Math.min(Math.max(1, page), totalPages);
    const WINDOW = 5;
    let start = Math.max(1, cur - Math.floor(WINDOW / 2));
    let end = Math.min(totalPages, start + WINDOW - 1);
    start = Math.max(1, end - WINDOW + 1);
    const pages: number[] = [];
    for (let i = start; i <= end; i++) pages.push(i);
    const go = (p: number) => { if (p >= 1 && p <= totalPages && p !== cur) onChangePage(p); };

    const Arrow = ({ name, enabled, onPress }: any) => (
        <TouchableOpacity disabled={!enabled} onPress={onPress}
            style={{ width: s(30), height: s(30), borderRadius: s(8), alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: T.border, marginHorizontal: s(2), opacity: enabled ? 1 : 0.35 }}>
            <Ionicons name={name} size={s(14)} color={T.textSub} />
        </TouchableOpacity>
    );

    return (
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', paddingVertical: s(14), paddingHorizontal: s(6), flexWrap: 'wrap', gap: s(10) }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(12) }}>
                {/* Rows per page */}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(6) }}>
                    <Text style={{ fontSize: s(13), color: T.textSub }}>Rows per page:</Text>
                    <View style={{ zIndex: 100 }}>
                        <TouchableOpacity onPress={() => setOpen(o => !o)}
                            style={{ flexDirection: 'row', alignItems: 'center', gap: s(6), paddingHorizontal: s(10), height: s(30), borderRadius: s(8), borderWidth: 1, borderColor: T.border, backgroundColor: T.cardInner }}>
                            <Text style={{ fontSize: s(13), color: T.text, fontWeight: '600' }}>{perPage}</Text>
                            <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={s(13)} color={T.textSub} />
                        </TouchableOpacity>
                        {open && (
                            <View style={{ position: 'absolute', bottom: s(34), left: 0, minWidth: s(72), backgroundColor: T.card, borderWidth: 1, borderColor: T.border, borderRadius: s(8), paddingVertical: s(4), zIndex: 200, shadowColor: '#000', shadowOpacity: 0.15, shadowRadius: s(8) } as any}>
                                {options.map((o) => (
                                    <TouchableOpacity key={o} onPress={() => { onChangePerPage(o); setOpen(false); }}
                                        style={{ paddingHorizontal: s(12), paddingVertical: s(7), backgroundColor: o === perPage ? accent + '22' : 'transparent' }}>
                                        <Text style={{ fontSize: s(13), color: o === perPage ? accent : T.text, fontWeight: o === perPage ? '700' : '500' }}>{o}</Text>
                                    </TouchableOpacity>
                                ))}
                            </View>
                        )}
                    </View>
                </View>
                {/* 페이지 이동 */}
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Arrow name="play-skip-back" enabled={cur > 1} onPress={() => go(1)} />
                    <Arrow name="chevron-back" enabled={cur > 1} onPress={() => go(cur - 1)} />
                    {pages.map((p) => (
                        <TouchableOpacity key={p} onPress={() => go(p)}
                            style={{ minWidth: s(30), height: s(30), paddingHorizontal: s(6), borderRadius: s(8), alignItems: 'center', justifyContent: 'center', marginHorizontal: s(2), backgroundColor: p === cur ? accent : 'transparent', borderWidth: p === cur ? 0 : 1, borderColor: T.border }}>
                            <Text style={{ fontSize: s(13), fontWeight: p === cur ? '800' : '600', color: p === cur ? '#fff' : T.textSub }}>{p}</Text>
                        </TouchableOpacity>
                    ))}
                    <Arrow name="chevron-forward" enabled={cur < totalPages} onPress={() => go(cur + 1)} />
                    <Arrow name="play-skip-forward" enabled={cur < totalPages} onPress={() => go(totalPages)} />
                </View>
            </View>
        </View>
    );
};

const fmt = (v: any) => {
    if (v == null || v === '') return '-';
    const n = Number(v);
    return isNaN(n) ? v : n.toLocaleString();
};
import { FontAwesome } from '@expo/vector-icons';

export const DetailHeader = ({ 
    focus, 
    onFocus, 
    page, 
    onSensorUpdate, 
    onSensorDelete, 
    onRepeaterUpdate, 
    onRepeaterDelete, 
    onRepeaterUpgrade,
    role,    // 추가
}: any) => {

    const isAdmin = role === 0;

    const onSave = () => {
        if (page === 'sensor') {
            onSensorUpdate();
        } else {
            onRepeaterUpdate();
        }
    };

    const onDelete = () => {
        if (page === 'sensor') {
            onSensorDelete();
        } else {
            onRepeaterDelete();
        }
    };


    return (
        <View style={styles.TopBox}>
            <View style={{ flexDirection: 'row', alignItems: 'center', paddingTop: 15 }}>
                <Text style={styles.title}>기본 정보</Text>
            </View>

            <View style={{ flexDirection: 'row', alignItems: 'center' }}>

                {/* 리피터 센서 업데이트 : 관리자만 */}
                {page === 'repeater' && isAdmin && (
                    <TouchableOpacity
                        onPress={() => onRepeaterUpgrade?.()}
                        style={[styles.modifyButton, { backgroundColor: '#673ab7' }]}>
                        <Text style={styles.buttonFont}>센서 업데이트</Text>
                    </TouchableOpacity>
                )}

                {/* 수정 : 관리자 / 유저 공통 */}
                <TouchableOpacity
                    onPress={onFocus}
                    style={styles.modifyButton}>
                    <Text style={styles.buttonFont}>{focus ? '취소' : '수정'}</Text>
                </TouchableOpacity>

                {/* 저장 : 관리자 / 유저 공통 */}
                <TouchableOpacity
                    onPress={onSave}
                    style={[styles.modifyButton, { backgroundColor: '#1890FF' }]}>
                    <Text style={styles.buttonFont}>저장</Text>
                </TouchableOpacity>

                {/* 삭제 */}
                {(
                    (page === 'sensor') || 
                    (page === 'repeater' && isAdmin)
                ) && (
                    <TouchableOpacity
                        onPress={onDelete}
                        style={[styles.modifyButton, { backgroundColor: '#2196F3' }]}>
                        <Text style={styles.buttonFont}>삭제</Text>
                    </TouchableOpacity>
                )}

            </View>
        </View>
    );
};

export const WorkState = (item: any) => {
    return (
        <View style={{ borderWidth: 0, width: 50, alignItems: 'center' }}>
            <FontAwesome name='circle' size={15} color={item == 1 ? 'green' : 'red'} />
        </View>
    )
};

export const sensorState = (item: any) => {
    var stateColor = item?.CONNECT == 0 && item?.BATTERY > 50 ? '#64dd17'
        : item?.CONNECT == 0 && item?.BATTERY > 20 ? '#ffa726'
            : item?.CONNECT == 0 && item?.BATTERY > 5 ? '#f52f2f'
                : 'lightgrey';

    return (
        <View style={{ borderWidth: 0, width: 50, alignItems: 'center' }}>
            <FontAwesome name='circle' size={15} color={stateColor} />
        </View>
    )
};

export const RepeaterState = (item: any) => {
    var stateColor = item == 0 ? '#64dd17' : 'lightgrey';

    return (
        <View style={{ borderWidth: 0, width: 50, alignItems: 'center' }}>
            <FontAwesome name='circle' size={15} color={stateColor} />
        </View>
    )
};

// ─── RSSI(신호세기) ────────────────────────────────────
// dBm(음수) → 강/중/약 색상. 0에 가까울수록 강함.
//   >= -75: 강(초록) / -75~-85: 중(주황) / < -85: 약(빨강) / 값없음: 회색
export const rssiInfo = (rssi: any) => {
    const v = (rssi === null || rssi === undefined || rssi === '' || isNaN(Number(rssi))) ? null : Number(rssi);
    if (v === null) return { text: '-', color: '#94A3B8', label: '' };
    const color = v >= -75 ? '#64dd17' : v >= -85 ? '#ffa726' : '#f52f2f';
    const label = v >= -75 ? '강' : v >= -85 ? '중' : '약';
    return { text: String(v), color, label };
};

// 테이블 셀용 — 신호 아이콘 + dBm 값(색상). 끊긴 센서도 마지막 신호를 표시
// (약해서 끊긴 건지 / 강한데 끊긴 건지(배터리) 진단용)
export const RssiCell = (item: any) => {
    const info = rssiInfo(item?.RSSI);
    return (
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Ionicons name='cellular' size={13} color={info.color} />
            <Text style={{ color: info.color, fontSize: 13, fontWeight: '600', marginLeft: 4 }}>{info.text}</Text>
        </View>
    )
};

// ─── 마지막 수신(=연결 끊긴 시점) ──────────────────────
// 백엔드 LAST_MIN(분, NOW()-마지막TIME, TZ안전)을 한국어 상대시간으로.
//   ≤5분: 초록(정상) / ≤60분: 주황 / >60분: 빨강(오래 끊김)
export const lastSeenInfo = (mins: any) => {
    if (mins === null || mins === undefined || mins === '' || isNaN(Number(mins)))
        return { text: '-', color: '#94A3B8' };
    const m = Math.max(0, Number(mins));
    const text = m < 1 ? '방금' : m < 60 ? `${m}분 전` : m < 1440 ? `${Math.floor(m / 60)}시간 전` : `${Math.floor(m / 1440)}일 전`;
    const color = m <= 5 ? '#64dd17' : m <= 60 ? '#ffa726' : '#f52f2f';
    return { text, color };
};

export const LastSeenCell = (item: any) => {
    const info = lastSeenInfo(item?.LAST_MIN);
    return (
        <Text style={{ color: info.color, fontSize: 12 }}>{info.text}</Text>
    )
};

export const managerColumns = [
    {
        id: 1,
        name: '이름',
        selector: (row: any) => row.NAME,
        grow: 3
    },
    {
        id: 2,
        name: '전화번호',
        selector: (row: any) => row.PHONE,
        grow: 3
    },
    {
        id: 3,
        name: '출퇴근상태',
        selector: (row: any) => WorkState(row.WORKING),
        grow: 1
    },
];

export const sensorColumns = [
    {
        id: 1,
        name: "no.",
        selector: (row: any, index: any) => index + 1,
        grow: 0
    },
    {
        id: 2,
        name: "이름",
        selector: (row: any) => row.NAME,
    },
    {
        id: 3,
        name: '성별',
        selector: (row: any) => row.GENDER,
    },
    {
        id: 4,
        name: '생년월일',
        selector: (row: any) => moment(row.BIRTH).format('YYYY-MM-DD'),
    },
    {
        id: 5,
        name: '병실 호수',
        selector: (row: any) => row.ROOM_NO,
    },
    {
        id: 6,
        name: "침대 no.",
        selector: (row: any) => row.BED_NO ? row.BED_NO : '',
    },
    {
        id: 7,
        name: '센서 ID',
        selector: (row: any) => row.SENSOR_ID,
    },
    {
        id: 8,
        name: '연결 상태',
        selector: (row: any) => sensorState(row),
    },
];

export const sensingColumns = [
    {
        id: 1,
        name: "날짜",
        selector: (row: any) => moment(row.REGDATE).format('YYYY-MM-DD'),
    },
    {
        id: 2,
        name: "시간",
        selector: (row: any) => moment(row.REGDATE).format('HH:mm:ss'),
    },
    {
        id: 3,
        name: '대소변 종류',
        selector: (row: any) => row.KIND == 0 ? '소변' : '대변',
    },
    {
        id: 4,
        name: '대소변량',
        selector: (row: any) => fmt(row.AMOUNT),
    },
    {
        id: 5,
        name: '센서값#1',
        selector: (row: any) => fmt(row.VALUE),
    },
    {
        id: 6,
        name: "연결 상태",
        selector: (row: any) => sensorState(row),
    },
];

export const sensingAlramColumns = [
    {
        id: 1,
        name: "날짜",
        selector: (row: any) => moment(row.REGDATE).format('YYYY-MM-DD'),
    },
    {
        id: 2,
        name: "시간",
        selector: (row: any) => moment(row.REGDATE).format('HH:mm:ss'),
    },
    {
        id: 3,
        name: "알림 코드",
        selector: (row: any) => fmt(row.AMOUNT),
    },
    {
        id: 4,
        name: '알림 종류',
        selector: (row: any) => {
            switch (row.AMOUNT) {
                case 2:
                    return '기준값';
                case -1:
                    return '교체';
                case -3:
                    return '초기화';
                case -4:
                    return '센서이상';
                case -5:
                    return '센서오염';
                case -6:
                    return '움직임없음';
                default:
                    return '';
            }
        },
    },
    {
        id: 5,
        name: '센서값#1',
        selector: (row: any) => fmt(row.VALUE),
    },
];

export const noticeColumns = [
    {
        id: 1,
        name: "no.",
        selector: (row: any) => row.IDX,
    },
    {
        id: 2,
        name: "제목",
        selector: (row: any) => row?.TITLE,
        grow: 2,
    },
    {
        id: 3,
        name: '내용',
        selector: (row: any) => row.CONTENT,
        grow: 2,
    },
    {
        id: 4,
        name: "등록일",
        selector: (row: any) => moment(row.REGDATE).format('YYYY-MM-DD'),
    },
];

export const caregiverPushAlarmColumns = [
    {
        id: 1,
        name: "no.",
        selector: (row: any, index: any) => row.ID,
        grow: 0
    },
    {
        id: 2,
        name: "전화번호",
        selector: (row: any) => row.PHONE,
    },
    {
        id: 3,
        name: '내용',
        selector: (row: any) => row.BODY,
        grow: 2,
    },
    {
        id: 4,
        name: "등록일",
        selector: (row: any) => moment(row.MSGDATE).format('YYYY-MM-DD HH:mm:ss'),
    },
];

const styles = StyleSheet.create({
    TopBox: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 20,
    },
    namebox: {
        flexDirection: 'row',
        flex: 1,
        alignItems: 'center',
        borderBottomWidth: 1,
        borderColor: '#dfe9f0',
        backgroundColor: '#dfe9f0',
    },
    titleName: {
        fontSize: 16,
        fontWeight: '500',
        color: '#000',
    },
    modifyButton: {
        paddingHorizontal: 20,
        paddingVertical: 6,
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 0,
        borderRadius: 10,
        backgroundColor: '#FF8A80',
        marginRight: 20
    },
    buttonFont: {
        fontSize: 16,
        fontWeight: '600',
        color: '#fff'
    },
    title: {
        marginLeft: 20,
        fontSize: 20,
        fontWeight: '600',
        color: '#202D3C'
    },
});
